import React from 'react';

import { AccountingEntry, AccountingNode } from '../../../../types';

interface FieldRefs {
  entryName?: React.RefObject<HTMLInputElement>;
  account?: React.RefObject<HTMLInputElement>;
  description?: React.RefObject<HTMLInputElement>;
  costCenter?: React.RefObject<HTMLInputElement>;
  documentKey?: React.RefObject<HTMLInputElement | HTMLDivElement>;
  businessIdentifier?: React.RefObject<HTMLInputElement>;
  debit?: React.RefObject<HTMLInputElement>;
  credit?: React.RefObject<HTMLInputElement>;
}

interface GetNextFieldParams {
  currentField: string;
  lineIndex: number;
  entry: AccountingEntry;
  nodes: AccountingNode[];
  free: boolean;
  handleAddLine: () => void;
}

interface FocusFieldParams {
  lineIndex: number;
  field: string;
  getLineRefs: (lineIndex: number) => FieldRefs;
}

export const getNextField = ({
  currentField,
  lineIndex,
  entry,
  nodes,
  free,
  handleAddLine,
}: GetNextFieldParams): { lineIndex: number; field: string } | null => {
  const line = entry.lines[lineIndex];
  const selectedNode = nodes.find(
    (node: AccountingNode) => node.id === line.nodeId,
  );

  const fieldOrder: string[] = [];
  if (free && lineIndex === 0) {
    fieldOrder.push('entryName');
  }
  fieldOrder.push('account', 'description');
  if (selectedNode?.costCenterRequired) {
    fieldOrder.push('costCenter');
  }
  if (selectedNode?.documentKeyRequired) {
    fieldOrder.push('documentKey');
  }
  if (selectedNode?.businessIdentifierRequired) {
    fieldOrder.push('businessIdentifier');
  }
  fieldOrder.push('debit', 'credit');

  const currentIndex = fieldOrder.indexOf(currentField);
  if (currentIndex === -1) return null;

  if (currentIndex < fieldOrder.length - 1) {
    return { lineIndex, field: fieldOrder[currentIndex + 1] };
  }

  if (currentField === 'credit') {
    if (lineIndex < entry.lines.length - 1) {
      const nextLineIndex = lineIndex + 1;
      const nextLine = entry.lines[nextLineIndex];
      const nextLineNode = nodes.find(
        (node: AccountingNode) => node.id === nextLine.nodeId,
      );
      const nextFieldOrder: string[] = ['account', 'description'];
      if (nextLineNode?.costCenterRequired) {
        nextFieldOrder.push('costCenter');
      }
      if (nextLineNode?.documentKeyRequired) {
        nextFieldOrder.push('documentKey');
      }
      if (nextLineNode?.businessIdentifierRequired) {
        nextFieldOrder.push('businessIdentifier');
      }
      nextFieldOrder.push('debit', 'credit');
      return { lineIndex: nextLineIndex, field: nextFieldOrder[0] };
    }
    handleAddLine();
    return null;
  }

  return null;
};

export const focusField = ({
  lineIndex,
  field,
  getLineRefs,
}: FocusFieldParams): void => {
  const lineRefs = getLineRefs(lineIndex);
  const fieldRef = lineRefs[field as keyof typeof lineRefs];

  // Special handling for Select components (documentKey)
  if (field === 'documentKey') {
    setTimeout(() => {
      // For Select, find the input element inside
      const selectElement = document.querySelector(
        `[data-line-index="${lineIndex}"][data-field="documentKey"]`,
      ) as HTMLElement;
      if (selectElement) {
        // Find the input element within the Select
        const inputElement =
          selectElement.querySelector('input[aria-hidden="false"]') ||
          selectElement.querySelector('input') ||
          selectElement;
        if (inputElement) {
          (inputElement as HTMLElement).focus();
          (inputElement as HTMLElement).click();
        }
      }
    }, 50);
    return;
  }

  if (fieldRef?.current) {
    setTimeout(() => {
      fieldRef.current?.focus();
    }, 0);
  }
};

interface HandleEnterPressParams {
  lineIndex: number;
  currentField: string;
  entry: AccountingEntry;
  nodes: AccountingNode[];
  free: boolean;
  handleAddLine: () => void;
  getLineRefs: (lineIndex: number) => FieldRefs;
}

export const handleEnterPress = ({
  lineIndex,
  currentField,
  entry,
  nodes,
  free,
  handleAddLine,
  getLineRefs,
}: HandleEnterPressParams): void => {
  const next = getNextField({
    currentField,
    lineIndex,
    entry,
    nodes,
    free,
    handleAddLine,
  });
  if (next) {
    // Small delay to ensure state is updated
    setTimeout(() => {
      focusField({
        lineIndex: next.lineIndex,
        field: next.field,
        getLineRefs,
      });
    }, 10);
  }
};
